// Location quality gate — filters out GPS fixes that would make a staff marker "jump" on the dashboard
// (typically the cell-tower / network fallback fix Android and iOS return when GPS is lost, e.g. in an elevator).

// ---- Layer 1: accuracy ----
// Cell-tower fixes are usually >= 300m, while valid GPS / Wi-Fi fixes indoors are usually <= 80m.
// Fixes worse than MAX_ACCURACY_METERS are dropped entirely; fixes between PRECISE_ACCURACY_METERS and
// MAX_ACCURACY_METERS are still shown on the dashboard but must not drive geofence inside/outside decisions.
export const MAX_ACCURACY_METERS = 100;
export const PRECISE_ACCURACY_METERS = 50;

// ---- Layer 2: freshness ----
const MAX_FIX_AGE_MS = 30_000;

// ---- Layer 3: jump detection ----
// A fix is a "jump" when it is farther from the last good fix than the staff could have travelled at the
// current speed ceiling. The ceiling is tight while walking/standing and only rises to the vehicle ceiling
// after recently *accepted* fixes showed vehicle-like speed (so a single bogus fix can never unlock it).
const WALKING_MAX_SPEED_MPS = 15;
const VEHICLE_MAX_SPEED_MPS = 40;       // ~144 km/h
const VEHICLE_ENTER_SPEED_MPS = 8;      // ~29 km/h, faster than anyone runs
const VEHICLE_WINDOW_MS = 60_000;       // how long vehicle mode lasts after the last fast accepted fix

// A suspected jump is only accepted once this many consecutive, mutually consistent fixes confirm it
// and they span at least JUMP_CONFIRM_MIN_SPAN_MS (a burst from the same bogus source is not a confirmation).
const JUMP_CONFIRM_READINGS = 3;
const JUMP_CONFIRM_MIN_SPAN_MS = 10_000;

// If the last good fix is older than this the baseline is considered expired and the next fix that passes
// layers 1-2 is accepted as the new baseline (covers long GPS gaps such as parking garages).
const LAST_GOOD_MAX_AGE_MS = 5 * 60_000;

export interface QualityFix {
    lat: number;
    lng: number;
    accuracy?: number | null;
    speed?: number | null;
    time?: number | null; // epoch ms
}

export type QualityRejectReason = 'low-accuracy' | 'stale' | 'jump-pending';

export interface QualityResult {
    accept: boolean;
    // True when accuracy is good enough (<= PRECISE_ACCURACY_METERS) to decide geofence inside/outside.
    precise: boolean;
    reason?: QualityRejectReason;
}

interface TrackedFix {
    lat: number;
    lng: number;
    accuracy: number;
    time: number;
}

export function distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const R = 6371000;
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const x = dLng * Math.cos((lat1 + lat2) / 2);
    const y = dLat;
    return Math.sqrt(x * x + y * y) * R;
}

export class LocationQualityGate {
    private lastGood: TrackedFix | null = null;
    private lastFastAt = 0;
    private candidateChain: TrackedFix[] = [];

    evaluate(fix: QualityFix, opts?: { speedCeilingOverride?: number }): QualityResult {
        const now = Date.now();
        const time = fix.time ?? now;
        const accuracy = fix.accuracy;

        // Layer 1 — accuracy (a negative value means "invalid" on iOS)
        if (accuracy == null || !isFinite(accuracy) || accuracy < 0 || accuracy > MAX_ACCURACY_METERS) {
            return this.reject('low-accuracy');
        }

        // Layer 2 — freshness
        if (now - time > MAX_FIX_AGE_MS) {
            return this.reject('stale');
        }

        const current: TrackedFix = { lat: fix.lat, lng: fix.lng, accuracy, time };
        const precise = accuracy <= PRECISE_ACCURACY_METERS;
        const last = this.lastGood;

        if (!last || time - last.time > LAST_GOOD_MAX_AGE_MS) {
            this.acceptAsBaseline(current);
            return { accept: true, precise };
        }

        // Older than the baseline (e.g. interleaved background/foreground callbacks) — never move backwards in time.
        if (time < last.time) {
            return this.reject('stale');
        }

        // Layer 3 — jump detection
        const dt = Math.max(1, (time - last.time) / 1000);
        const ceiling = opts?.speedCeilingOverride ?? this.speedCeiling(last.time);
        const dist = distanceMeters(last, current);
        const allowed = ceiling * dt + last.accuracy + accuracy;

        if (dist <= allowed) {
            this.recordObservedSpeed(fix.speed, dist, dt, last.accuracy + accuracy, time);
            this.acceptAsBaseline(current);
            return { accept: true, precise };
        }

        if (this.confirmCandidate(current, ceiling)) {
            this.acceptAsBaseline(current);
            return { accept: true, precise };
        }
        return this.reject('jump-pending');
    }

    reset() {
        this.lastGood = null;
        this.lastFastAt = 0;
        this.candidateChain = [];
    }

    private reject(reason: QualityRejectReason): QualityResult {
        return { accept: false, precise: false, reason };
    }

    private acceptAsBaseline(fix: TrackedFix) {
        this.lastGood = fix;
        this.candidateChain = [];
    }

    // Evaluated at the time of the last good fix (not the new one): "was the staff moving like a vehicle as of
    // the last accepted fix?" — so vehicle mode survives a GPS gap (tunnel) instead of expiring during it.
    private speedCeiling(lastGoodTime: number): number {
        return lastGoodTime - this.lastFastAt <= VEHICLE_WINDOW_MS ? VEHICLE_MAX_SPEED_MPS : WALKING_MAX_SPEED_MPS;
    }

    // Prefer the Doppler speed reported by the plugin; otherwise derive it from the two accepted fixes, minus
    // their combined accuracy so ordinary GPS jitter over a short interval cannot fake vehicle-like speed.
    private recordObservedSpeed(reported: number | null | undefined, dist: number, dt: number, slack: number, at: number) {
        const observed = reported != null && isFinite(reported) && reported >= 0
            ? reported
            : Math.max(0, dist - slack) / dt;
        if (observed > VEHICLE_ENTER_SPEED_MPS) {
            this.lastFastAt = at;
        }
    }

    // Returns true when the suspected jump has been confirmed by consecutive consistent fixes.
    private confirmCandidate(current: TrackedFix, ceiling: number): boolean {
        const chain = this.candidateChain;
        const prev = chain[chain.length - 1];

        if (prev) {
            const dtPrev = Math.max(1, (current.time - prev.time) / 1000);
            const consistent = current.time >= prev.time
                && distanceMeters(prev, current) <= ceiling * dtPrev + prev.accuracy + current.accuracy;
            if (!consistent) {
                this.candidateChain = [current];
                return false;
            }
        }

        chain.push(current);
        return chain.length >= JUMP_CONFIRM_READINGS
            && current.time - chain[0].time >= JUMP_CONFIRM_MIN_SPAN_MS;
    }
}
